/**
 * Budget WRITE endpoints — request schemas (Pattern B).
 *
 * Every browser write on the five budget tables (`envelope_budgets`,
 * `budget_category_groups`, `budget_categories`, `budget_category_entries`,
 * `budget_month_allocations`) goes through the Hono router
 * `apps/api/src/routes/accounting/budgets.ts` (mounted at `/api/v1/budgets`),
 * which runs each statement through the per-request backend-token client.
 * The `*_budgets_backend_write_lockdown.sql` migration then adds RESTRICTIVE
 * `verify_backend_token()` policies so a raw PostgREST write is refused —
 * see docs/BACKEND_API.md → "Pattern B" and docs/BUDGETS.md.
 *
 * Every body is `.strict()`: `organization_id`, `budget_id` and `created_by`
 * come from the URL / the authenticated user, never the body, so a body that
 * carries one is a 400 rather than a silently ignored key.
 *
 * Plan-shaped budgets (Ré's `create_budget` tool) are validated by
 * `budgets.ts` in this folder; this file is the plain CRUD surface.
 *
 * Month fields are `calendarDateSchema` (a real `yyyy-MM-dd` day) rather than
 * `firstOfMonthSchema`: the web already normalises them to the first of the
 * month, and the tables do not constrain it — tightening that here would be a
 * behaviour change outside this move.
 */
import { z } from "zod";
import { BUDGET_TYPES } from "./budgets.js";
import { calendarDateSchema } from "./dateWindow.js";
/** `envelope_budgets_status_check`. */
export const BUDGET_STATUSES = ["active", "closed", "archived"];
/** A signed amount inside numeric(15,2). */
const budgetAmount = z.number().finite().min(-9_999_999_999_999).max(9_999_999_999_999);
const nonEmpty = (v) => Object.keys(v).length > 0;
const NON_EMPTY = { message: "At least one field must be provided" };
// ---- envelope_budgets -------------------------------------------------------
/** POST /orgs/:orgId/budgets */
export const createEnvelopeBudgetSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(2000).nullable().optional(),
    status: z.enum(BUDGET_STATUSES).optional(),
    budget_type: z.enum(BUDGET_TYPES).optional(),
    project_id: z.string().uuid().nullable().optional(),
    fund_id: z.string().uuid().nullable().optional(),
    start_month: calendarDateSchema,
    end_month: calendarDateSchema.nullable().optional(),
})
    .strict();
/**
 * PATCH /orgs/:orgId/budgets/:budgetId — any subset. `project_id` and
 * `budget_type` are set at creation only (no UI edits them).
 */
export const updateEnvelopeBudgetSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(2000).nullable(),
    status: z.enum(BUDGET_STATUSES),
    fund_id: z.string().uuid().nullable(),
    start_month: calendarDateSchema,
    end_month: calendarDateSchema.nullable(),
    locked_through_month: calendarDateSchema.nullable(),
})
    .partial()
    .strict()
    .refine(nonEmpty, NON_EMPTY);
// ---- budget_category_groups -------------------------------------------------
/** POST /orgs/:orgId/budgets/:budgetId/groups */
export const createBudgetCategoryGroupSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    is_income: z.boolean().optional(),
    sort_order: z.number().int().optional(),
})
    .strict();
/** PATCH /orgs/:orgId/budgets/:budgetId/groups/:groupId */
export const updateBudgetCategoryGroupSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    sort_order: z.number().int(),
    is_hidden: z.boolean(),
    is_income: z.boolean(),
})
    .partial()
    .strict()
    .refine(nonEmpty, NON_EMPTY);
// ---- budget_categories ------------------------------------------------------
/**
 * POST /orgs/:orgId/budgets/:budgetId/categories. `group_id` must be a group
 * of the same budget (checked by the handler — the FK alone would accept any
 * group); `gl_account_id` is checked by the `guard_budget_category_account_org`
 * trigger.
 */
export const createBudgetCategorySchema = z
    .object({
    group_id: z.string().uuid(),
    name: z.string().trim().min(1).max(200),
    gl_account_id: z.string().uuid().nullable().optional(),
    sort_order: z.number().int().optional(),
    rollover_overspending: z.boolean().optional(),
    default_budgeted: budgetAmount.nullable().optional(),
    total_budgeted: budgetAmount.optional(),
})
    .strict();
/** PATCH /orgs/:orgId/budgets/:budgetId/categories/:categoryId */
export const updateBudgetCategorySchema = z
    .object({
    group_id: z.string().uuid(),
    name: z.string().trim().min(1).max(200),
    gl_account_id: z.string().uuid().nullable(),
    sort_order: z.number().int(),
    is_hidden: z.boolean(),
    rollover_overspending: z.boolean(),
    notes: z.string().max(5000).nullable(),
    default_budgeted: budgetAmount.nullable(),
    total_budgeted: budgetAmount,
})
    .partial()
    .strict()
    .refine(nonEmpty, NON_EMPTY);
// ---- budget_month_allocations (via the two INVOKER RPCs) --------------------
/**
 * POST /orgs/:orgId/budgets/:budgetId/allocations — the body handed to
 * `upsert_budget_allocations(p_budget_id, p_changes)` unchanged. `budgeted:
 * null` deletes the explicit allocation (the category falls back to its
 * `default_budgeted`).
 */
export const upsertBudgetAllocationsSchema = z
    .object({
    changes: z
        .array(z
        .object({
        category_id: z.string().uuid(),
        month: calendarDateSchema,
        budgeted: budgetAmount.nullable(),
    })
        .strict())
        .min(1)
        .max(5000),
})
    .strict();
/**
 * POST /orgs/:orgId/budgets/:budgetId/transfers — `transfer_budget_allocation`.
 * The RPC re-checks amount > 0, source ≠ target, both in the budget, and the
 * available balance.
 */
export const transferBudgetAllocationSchema = z
    .object({
    source_category_id: z.string().uuid(),
    target_category_id: z.string().uuid(),
    month: calendarDateSchema,
    amount: z.number().finite().positive().max(9_999_999_999_999),
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
// ---- budget_category_entries (ledger-entry budget tags) ---------------------
/**
 * Max tags per category-entries request. The web hooks chunk larger saves /
 * deletes into sequential requests of at most this many.
 */
export const BUDGET_ENTRY_BATCH_MAX = 500;
/**
 * POST /orgs/:orgId/category-entries — upsert tags on
 * `(ledger_entry_id, budget_category_id)`. Both ids are checked against the
 * URL org by the handler.
 */
export const saveBudgetCategoryEntriesSchema = z
    .object({
    entries: z
        .array(z
        .object({
        ledger_entry_id: z.string().uuid(),
        budget_category_id: z.string().uuid(),
    })
        .strict())
        .min(1)
        .max(BUDGET_ENTRY_BATCH_MAX),
})
    .strict();
/** POST /orgs/:orgId/category-entries/delete — every tag on these ledger entries. */
export const deleteBudgetCategoryEntriesSchema = z
    .object({
    ledger_entry_ids: z.array(z.string().uuid()).min(1).max(BUDGET_ENTRY_BATCH_MAX),
})
    .strict();
//# sourceMappingURL=budgetsWrites.js.map